import { CONTACT, STORE_LINKS } from '@/constants/site.constants';
import type {
  CapabilityCard,
  ContactTile,
  DownloadCopy,
  FaqItem,
  HeroCopy,
  HeroHeadline,
  HowItWorksStep,
  MoneyPanel,
  PhoneScreen,
  ProblemCard,
  RentCategory,
  RentNote,
  SectionIntro,
  SiteLink,
  StoreBadge,
  TrustIconKey,
  TrustPoint,
  WhyShoutt,
  WhyUsPoint,
} from '@/types/home.types';

export const SECTION_IDS = {
  top: 'top',
  story: 'story',
  what: 'what',
  rent: 'rent',
  howItWorks: 'how',
  trust: 'trust',
  faq: 'faq',
  download: 'download',
  contact: 'contact',
} as const;

/* Anchors are written absolute (`/#what`) so a header link still works from a
   legal page — React Router leaves the document, then the browser scrolls. */
export const HEADER_LINKS: SiteLink[] = [
  { label: 'What you can do', href: `/#${SECTION_IDS.what}` },
  { label: 'How it works', href: `/#${SECTION_IDS.howItWorks}` },
  { label: 'Trust', href: `/#${SECTION_IDS.trust}` },
  { label: 'FAQ', href: `/#${SECTION_IDS.faq}` },
  { label: 'Contact', href: `/#${SECTION_IDS.contact}` },
];

export const HEADER_CTA: SiteLink = {
  label: 'Download the app',
  href: `/#${SECTION_IDS.download}`,
};

export const FOOTER_PRODUCT_LINKS: SiteLink[] = [
  { label: 'What you can do', href: `/#${SECTION_IDS.what}` },
  { label: 'How it works', href: `/#${SECTION_IDS.howItWorks}` },
  { label: 'Trust', href: `/#${SECTION_IDS.trust}` },
  { label: 'FAQ', href: `/#${SECTION_IDS.faq}` },
];

export const HERO_EYEBROW = 'For college-verified students';

export const HERO_HEADLINE: HeroHeadline = {
  lead: 'Your campus has everything.',
  accent: "It just can't find You.",
};

export const HERO_COPY: HeroCopy = {
  problem:
    "Somewhere nearby, a projector is gathering dust, a classmate is dying to edit your video, and someone needs a badminton partner at 6 PM. WhatsApp groups are chaos. Other apps have no idea you're a student. Notice boards? Retired.",
  builtPrefix: 'So, we built ',
  builtBrand: 'SHOUTT',
  pullQuote:
    "Rent out what you're not using and earn. Need something? Post a Shoutt and a verified student nearby picks it up.",
  outcome:
    'Idle stuff starts earning, urgent needs get met in minutes, and strangers start feeling like a community.',
  kicker: 'Powered by people around you.',
};

export const PHONE_SCREENS: PhoneScreen[] = [
  {
    id: 'feed',
    label: 'Shoutt feed',
    src: '/app-shoutt-feed.png',
    alt: 'Shoutts feed in the Shoutt app: Ask, Offer, Team and Community posts',
  },
  {
    id: 'rentals',
    label: 'Rentals',
    src: '/app-rentals.png',
    alt: 'Browse rentals in the Shoutt app: items on your campus with price per day and distance',
  },
];

export const STORE_BADGES: StoreBadge[] = [
  {
    platform: 'ios',
    storeName: 'App Store',
    scanLabel: 'Scan for iPhone',
    osLabel: 'iOS',
    qrSrc: '/qr-app-store.png',
    qrAlt: 'QR code for Shoutt on the App Store',
    href: STORE_LINKS.ios,
  },
  {
    platform: 'android',
    storeName: 'Google Play',
    scanLabel: 'Scan for Android',
    osLabel: 'Android',
    qrSrc: '/qr-google-play.png',
    qrAlt: 'QR code for Shoutt on Google Play',
    href: STORE_LINKS.android,
  },
];

export const PROBLEM_HEADLINE = "Your campus already has it. There's just no good way to find it.";

export const PROBLEM_CARDS: ProblemCard[] = [
  {
    title: 'WhatsApp groups',
    body: 'Chaotic, and your message is buried in minutes.',
  },
  {
    title: 'Notice boards',
    body: 'Nobody reads them anymore.',
  },
];

export const WHY_SHOUTT: WhyShoutt = {
  eyebrow: 'Why we built Shoutt',
  lead: "Every campus already has the things, the skills and the people willing to help. What it doesn't have is a trusted way to connect them.",
  body: "Shoutt is that layer: one verified app that makes what's idle on campus easy to find and safe to share.",
};

export const WHY_US_POINTS: WhyUsPoint[] = [
  {
    step: '01',
    title: 'Lived experience',
    body: 'We are college students and Gen Z ourselves, facing the same campus problems we aim to solve.',
  },
  {
    step: '02',
    title: 'Closer to the real problem',
    body: "We understand how students actually find help, share resources, and make decisions because we experience the gaps firsthand, and we've held real discovery conversations across multiple campuses.",
  },
  {
    step: '03',
    title: 'Built by students, for students',
    body: 'Our team brings ops, development, marketing, and finance together to turn real student needs into a trusted, usable solution.',
  },
];

export const CAPABILITY_INTRO: SectionIntro = {
  eyebrow: 'What you can do on Shoutt',
  title: 'Two sides of one app.',
  body: 'Rentals for things. The Shoutt feed for everything else.',
};

export const CAPABILITY_CARDS: CapabilityCard[] = [
  {
    tag: 'Rentals',
    tone: 'accent',
    index: '01',
    title: 'Rent it for a day',
    body: 'Cameras, cycles, projectors, lab kits, formal wear, consoles, textbooks. Pick your dates, see the total up front, book.',
    example: 'Canon 200D · ₹350/day · 180m',
  },
  {
    tag: 'Rentals',
    tone: 'accent',
    index: '02',
    title: "Earn from what you don't use",
    body: 'List anything gathering dust. Add photos and a short video, set a daily price, and block out the dates you need it yourself.',
    example: 'PS5 + 2 controllers · ₹500/day',
  },
  {
    tag: 'Shoutt · Ask',
    tone: 'neutral',
    index: '03',
    title: 'Ask your campus',
    body: "Can't find it in Rentals? Post a Shoutt with what you need and by when. Mark it urgent when it can't wait.",
    example: '"Need a projector for tonight" → Shoutt back',
  },
  {
    tag: 'Shoutt · Offer',
    tone: 'neutral',
    index: '04',
    title: 'Offer a skill',
    body: 'Have something the campus needs? Offer an item or a skill.',
    example: '"Free Figma lessons this Saturday" → I\'m in',
  },
  {
    tag: 'Shoutt · Team',
    tone: 'neutral',
    index: '05',
    title: 'Build a team',
    body: 'Find teammates for a hackathon, a pitch or a project.',
    example: '"Need 2 CS students for E-Cell pitch" → Join',
  },
  {
    tag: 'Shoutt · Community',
    tone: 'neutral',
    index: '06',
    title: 'Find your people',
    body: 'Or just start something with people around you.',
    example: '"Movie night on the hostel lawn" → I\'m in',
  },
];

export const RENT_INTRO: SectionIntro = {
  eyebrow: "What's on Shoutt",
  title: 'If a student owns it, it can be rented.',
};

export const RENT_CATEGORIES: RentCategory[] = [
  { title: 'Electronics', body: 'Cameras, drones, laptops, power banks, ring lights, speakers' },
  { title: 'Getting around', body: 'Cycles, scooters and bikes, for a day or a semester' },
  { title: 'Academics', body: "Course books, seniors' notes, lab and EGD kits, calculators" },
  { title: 'Fashion', body: 'Blazers, kurtas, festive and formal wear, accessories' },
  { title: 'Hostel life', body: 'Induction stoves, water coolers, bean bags, movable furniture' },
  { title: 'Games & sports', body: 'PS5s, board games, rackets and sports gear' },
  { title: 'Skills', body: 'Lessons, design help, mentoring, assignment support' },
  {
    title: 'For a day or two weeks',
    body: 'Pick your dates. Return on time, get your deposit back and earn Shoutt Coins.',
    highlighted: true,
  },
];

export const RENT_NOTES: RentNote[] = [
  {
    title: 'Last-minute needs',
    body: 'A power bank before a trip. A calculator the night before an exam.',
  },
  {
    title: 'Fests and seasons',
    body: 'Navratri outfits, fest gear, and moving in or out of the hostel.',
  },
  {
    title: 'Earning on the side',
    body: 'Lend the console you only play on weekends. No job needed.',
  },
];

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    step: '1',
    title: 'Sign up with your college email',
    body: 'One OTP puts you on your campus. Only students from your college can see and book your stuff.',
  },
  {
    step: '2',
    title: 'List it, or Shoutt for it',
    body: 'List what you own with a per-day price, or post a Shoutt and let the campus answer.',
  },
  {
    step: '3',
    title: 'Book, meet, return',
    body: 'Pay rent and a refundable deposit in the app, then meet on campus to collect. Return on time and the deposit comes back.',
  },
];

export const TRUST_INTRO: SectionIntro = {
  eyebrow: 'Built for trust',
  title: 'Only verified students. Only your campus.',
};

/** Lucide-style 24×24 outlines, kept here so the markup stays readable. */
export const TRUST_ICON_PATHS: Record<TrustIconKey, string[]> = {
  shield: [
    'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    'm9 12 2 2 4-4',
  ],
  star: ['M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z'],
  card: ['M2 6h20v12H2z', 'M14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0z', 'M6 12h.01M18 12h.01'],
  alert: [
    'M12 9v4',
    'M12 17h.01',
    'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  ],
  chat: ['M7.9 20A9 9 0 1 0 4 16.1L2 22z'],
  search: ['M19 11a8 8 0 1 1-16 0 8 8 0 0 1 16 0z', 'm21 21-4.3-4.3'],
};

export const TRUST_POINTS: TrustPoint[] = [
  {
    icon: 'shield',
    title: 'Campus-verified accounts',
    body: 'Every account is tied to a college email. Profiles show name, department and year.',
  },
  {
    icon: 'star',
    title: 'A Trust Score you build',
    body: 'Completed rentals, on-time returns and ratings add up to a score both sides see.',
  },
  {
    icon: 'card',
    title: 'Refundable security deposit',
    body: 'Held by Shoutt for the booking, refunded once the owner confirms the item is back.',
  },
  {
    icon: 'alert',
    title: 'Damage reports, real reviews',
    body: 'Owners report damage with photos. A real person reviews both sides before any deduction.',
  },
  {
    icon: 'chat',
    title: 'Chat built for handovers',
    body: 'Agree on a spot in the app and share live location while you walk over. No phone numbers.',
  },
  {
    icon: 'search',
    title: 'Moderation on every post',
    body: 'Listings and Shoutts are screened, and anything reported goes to human review.',
  },
];

export const MONEY_PANELS: MoneyPanel[] = [
  {
    eyebrow: 'Transparent money',
    title: 'Every rupee on screen before you pay',
    paragraphs: [
      'Rent, deposit, platform fee and GST, broken down up front. Listing is free. Payments go over UPI through Shoutt, never directly between students.',
      "Owners have 24 hours to confirm a booking. If they don't, you're refunded in full within 24–48 hours.",
    ],
  },
  {
    eyebrow: 'Shoutt Coins',
    title: 'Earn Shoutt Coins. Spend them in the app.',
    paragraphs: [
      'Collect Shoutt Coins for completing your profile, listing an item, finishing your first rental, returning on time, leaving honest reviews and inviting friends. Spend them to mark a Shoutt urgent, push it to the top, or cut your booking fee.',
    ],
  },
];

export const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'Who can join Shoutt?',
    answer:
      "Any student with a valid college email at a campus we've opened. Your campus is detected from your email domain when you verify with an OTP.",
  },
  {
    question: 'Is my college live?',
    answer:
      "Shoutt is rolling out campus by campus in India. If yours isn't live yet, write to hello@shoutt.live and we'll tell you when it is.",
  },
  {
    question: 'What if something comes back damaged?',
    answer:
      'Every booking has a refundable deposit held by Shoutt. The owner files a damage report with photos, and our team reviews both sides before any deduction.',
  },
  {
    question: 'How do payments work?',
    answer:
      'You pay rent and deposit in the app over UPI, with the full breakdown shown first. Money never passes directly between students.',
  },
  {
    question: 'What are Shoutt Coins?',
    answer:
      'They are rewards you earn for being active on Shoutt: listing, renting, returning on time, reviews and referrals. Use them to boost a Shoutt or lower platform fees.',
  },
  {
    question: 'When do owners get paid?',
    answer: 'Owners receive their payout after the rental is completed. Listing is free.',
  },
  {
    question: "What if the owner doesn't respond?",
    answer:
      "Owners have 24 hours to confirm a paid booking. If they don't, it's cancelled and you're refunded in full within 24–48 hours.",
  },
];

export const DOWNLOAD_COPY: DownloadCopy = {
  lead: 'Ask your campus.',
  accent: 'Get Shoutt.',
  note: 'Rolling out campus by campus. College not live yet?',
};

export const CONTACT_TILES: ContactTile[] = [
  {
    label: 'Instagram',
    value: CONTACT.instagramHandle,
    href: CONTACT.instagramUrl,
    external: true,
  },
  { label: 'Phone', value: CONTACT.phone, href: CONTACT.phoneHref },
  { label: 'Founders', value: CONTACT.foundersEmail, href: `mailto:${CONTACT.foundersEmail}` },
];
